import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth } from '../../contexts/AuthContext';
import { useModuloFiscalData } from './useModuloFiscalData';
import { avaliarPermissaoEtapaProcesso } from '../contratos/utils/processoEtapaPermissao';
import { normalizarTipoAcaoEtapa } from '../contratos/utils/fluxoEtapaTipos';
import PainelEtapaAprovacaoFluxo from '../../components/contratos/PainelEtapaAprovacaoFluxo';
import PainelEditalContratacao from '../../components/contratos/PainelEditalContratacao';
import EditorTRModal from '../../components/contratos/EditorTRModal';
import ProcessoHistoricoTimeline from '../../components/contratos/ProcessoHistoricoTimeline';
import MapaComparativoPropostasPanel from '../../components/contratos/MapaComparativoPropostasPanel';
import TrilhaStepperHeader, {
  fraseObjetivoEtapa,
  resolverPainelEtapa,
} from './TrilhaStepperHeader';
import './moduloFiscal.css';

function formatDt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Detalhe do processo — Fase 1 UX:
 * stepper + um único painel por tipoAcao + abas Etapa/Histórico.
 * Segurança: estado só em memória (React); persistência exclusiva via API/tenant.
 */
export default function ModuloFiscalProcessoDetalhePage() {
  const { fiscalId, processoId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada, user, rbacRole } = useAuth();
  const { loading: loadingBase, error: errorBase, fiscal } = useModuloFiscalData(fiscalId);

  const [loading, setLoading] = useState(true);
  const [processo, setProcesso] = useState(null);
  const [etapas, setEtapas] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineMeta, setTimelineMeta] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [msg, setMsg] = useState('');
  const [movendo, setMovendo] = useState(false);
  const [minutaOpen, setMinutaOpen] = useState(false);
  const [aba, setAba] = useState('etapa'); // 'etapa' | 'historico'
  const [etapaSelecionadaId, setEtapaSelecionadaId] = useState(null);
  const [concluidasOpen, setConcluidasOpen] = useState(false);
  const [anexosEtapa, setAnexosEtapa] = useState([]);
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);

  const basePath = `/modulos/fiscal/${fiscalId}/processo-administrativo`;

  const podeGerenciar =
    rbacRole === 'SUPER_ADMIN' ||
    rbacRole === 'LOCAL_ADMIN' ||
    rbacRole === 'GLOBAL_VIEWER';

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const [resP, resT, resF, resU] = await Promise.all([
        apiFetch(`/api/contratos/processos/${processoId}`),
        apiFetch(`/api/contratos/processos/${processoId}/timeline`),
        apiFetch(withUnidade('/api/contratos/fluxos', unidadeSelecionada)),
        apiFetch(
          `/api/usuarios${
            unidadeSelecionada?.codigo
              ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
              : ''
          }`
        ),
      ]);
      const dataP = await resP.json().catch(() => ({}));
      const dataT = await resT.json().catch(() => ({}));
      const dataF = await resF.json().catch(() => ([]));
      const dataU = await resU.json().catch(() => ([]));

      if (!resP.ok) throw new Error(dataP.error || 'Processo não encontrado');

      setProcesso(dataP);
      const tl = Array.isArray(dataT) ? dataT : dataT?.timeline || dataT?.historico || [];
      setTimeline(tl);
      setTimelineMeta({
        tempoTotalDias: dataT?.tempoTotalDias,
        tempoTotalHoras: dataT?.tempoTotalHoras,
      });
      setUsuarios(Array.isArray(dataU) ? dataU : []);

      const fluxos = Array.isArray(dataF) ? dataF : dataF?.fluxos || [];
      const fluxo =
        fluxos.find((f) => Number(f.id) === Number(dataP.fluxoId || dataP.fluxo_id)) ||
        fluxos.find((f) => f.ativo === 1 || f.ativo === true);
      const ets = fluxo?.etapas || fluxo?.etapasDefinicao || [];
      setEtapas(Array.isArray(ets) ? ets : []);
      setEtapaSelecionadaId(null);
    } catch (e) {
      console.error(e);
      setMsg(e.message || 'Erro ao carregar processo');
      setProcesso(null);
    } finally {
      setLoading(false);
    }
  }, [processoId, unidadeSelecionada]);

  useEffect(() => {
    if (!loadingBase && fiscal) load();
  }, [loadingBase, fiscal, load]);

  const etapaAtual = processo?.etapaAtual || {
    id: processo?.etapaId || processo?.etapa_atual_id,
    nome: processo?.etapaNome || processo?.etapa_nome,
    tipoAcao: processo?.etapaTipoAcao || processo?.etapa_tipo_acao,
    responsaveisIds: processo?.etapaResponsaveisIds || processo?.etapa_responsaveis_ids,
    limiteReaberturas: processo?.etapaLimiteReaberturas || processo?.etapa_limite_reaberturas || 3,
    prazoAlertaDias: processo?.etapaPrazoAlertaDias || processo?.etapa_prazo_alerta_dias || 3,
    exigeConfiguracaoEdital: processo?.exigeConfiguracaoEdital,
  };

  const etapaDefFluxo = useMemo(
    () => etapas.find((e) => Number(e.id) === Number(etapaAtual?.id)) || null,
    [etapas, etapaAtual?.id]
  );

  const tipoAcao = normalizarTipoAcaoEtapa(
    etapaAtual?.tipoAcao ||
      etapaAtual?.tipo_acao ||
      etapaDefFluxo?.tipoAcao ||
      etapaDefFluxo?.tipo_acao,
    {
      exigeEdital: !!(
        etapaAtual?.exigeConfiguracaoEdital ||
        etapaAtual?.exige_configuracao_edital ||
        etapaDefFluxo?.exigeConfiguracaoEdital ||
        etapaDefFluxo?.exige_configuracao_edital ||
        String(etapaAtual?.nome || etapaDefFluxo?.nome || '')
          .toLowerCase()
          .includes('edital')
      ),
      exigeAssinatura:
        etapaAtual?.exigeAssinatura != null
          ? etapaAtual.exigeAssinatura
          : etapaDefFluxo?.exigeAssinatura,
    }
  );
  const painel = resolverPainelEtapa(tipoAcao);

  const perm = useMemo(
    () =>
      avaliarPermissaoEtapaProcesso({
        user,
        etapa: etapaAtual,
        podeGerenciarProcessos: podeGerenciar,
        usuarios,
      }),
    [user, etapaAtual, podeGerenciar, usuarios]
  );

  const etapasTrilha = useMemo(() => {
    if (timeline.length) {
      return timeline.map((t) => ({
        id: t.id,
        nome: t.nome,
        ordem: t.ordem,
        status: t.status,
        ehAtual: t.ehAtual,
        iniciadoEm: t.iniciadoEm,
        concluidoEm: t.concluidoEm,
        eventos: t.eventos,
        grupoResponsavel: t.grupoResponsavel,
      }));
    }
    const atualId = Number(etapaAtual?.id);
    const atualOrdem = Number(
      etapas.find((e) => Number(e.id) === atualId)?.ordem ??
        etapas.find((e) => Number(e.id) === atualId)?.ordemEtapa ??
        0
    );
    return [...etapas]
      .sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0))
      .map((e) => {
        const ordem = Number(e.ordem ?? e.ordemEtapa ?? 0);
        const isAtual = Number(e.id) === atualId;
        return {
          id: e.id,
          nome: e.nome,
          ordem,
          ehAtual: isAtual,
          status: isAtual ? 'EM_ANDAMENTO' : ordem < atualOrdem ? 'CONCLUIDO' : 'PENDENTE',
          tipoAcao: e.tipoAcao || e.tipo_acao,
        };
      });
  }, [timeline, etapas, etapaAtual?.id]);

  const etapasConcluidas = useMemo(
    () => etapasTrilha.filter((e) => String(e.status).toUpperCase() === 'CONCLUIDO' && !e.ehAtual),
    [etapasTrilha]
  );

  const etapaSelecionada = useMemo(() => {
    const id = etapaSelecionadaId != null ? Number(etapaSelecionadaId) : Number(etapaAtual?.id);
    return etapasTrilha.find((e) => Number(e.id) === id) || null;
  }, [etapasTrilha, etapaSelecionadaId, etapaAtual?.id]);

  const vendoEtapaAtual =
    etapaSelecionadaId == null || Number(etapaSelecionadaId) === Number(etapaAtual?.id);

  const proximaEtapa = useMemo(() => {
    const atualId = Number(etapaAtual?.id);
    const atual = etapas.find((e) => Number(e.id) === atualId);
    if (!atual) return null;
    const ordem = Number(atual.ordem ?? atual.ordemEtapa ?? 0);
    return (
      [...etapas]
        .filter((e) => Number(e.ordem ?? e.ordemEtapa ?? 0) > ordem)
        .sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0))[0] || null
    );
  }, [etapas, etapaAtual?.id]);

  const primeiraEtapa = useMemo(() => {
    return [...etapas].sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0))[0] || null;
  }, [etapas]);

  const executarMover = async ({ acao, observacao }) => {
    if (!processo) return;
    const rejeitar = String(acao).toUpperCase() === 'REJEITAR';
    const destino = rejeitar ? primeiraEtapa : proximaEtapa;
    if (!destino?.id && !rejeitar) {
      setMsg('Não há próxima etapa no fluxo. Processo pode estar na etapa final (minuta/Prestadores).');
      return;
    }

    setMovendo(true);
    setMsg('');
    try {
      const formData = new FormData();
      formData.append('etapaDestinoId', String(destino?.id || etapaAtual?.id || ''));
      formData.append('acao', rejeitar ? 'REJEITAR' : 'MOVER');
      formData.append('observacao', observacao || '');
      formData.append('usuario', user?.nome || user?.email || '');
      formData.append('status', '');
      formData.append('voltarInicio', rejeitar ? 'true' : 'false');
      if (user?.email) formData.append('usuarioEmail', user.email);
      if (user?.tipo) formData.append('usuarioTipo', user.tipo);
      if (user?.setor || fiscal?.setor) formData.append('usuarioSetor', user?.setor || fiscal?.setor);
      (anexosEtapa || []).forEach((file) => formData.append('anexo', file));

      const res = await apiFetch(`/api/contratos/processos/${processo.id}/mover`, {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao avançar etapa');

      setAnexosEtapa([]);
      setMsg(rejeitar ? 'Processo devolvido conforme fluxo.' : 'Etapa avançada.');
      await load();
    } catch (e) {
      setMsg(e.message || 'Erro ao mover processo');
      throw e;
    } finally {
      setMovendo(false);
    }
  };

  const enviarAnexosAgora = async () => {
    if (!processo?.id || !anexosEtapa.length) {
      setMsg('Selecione ao menos um PDF para anexar (opcional).');
      return;
    }
    setEnviandoAnexo(true);
    setMsg('');
    try {
      const fd = new FormData();
      if (etapaAtual?.id) fd.append('etapaId', String(etapaAtual.id));
      anexosEtapa.forEach((f) => fd.append('anexo', f));
      const res = await apiFetch(`/api/contratos/processos/${processo.id}/arquivos`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao anexar');
      setAnexosEtapa([]);
      setMsg(data.message || 'Documento(s) anexado(s).');
      await load();
    } catch (e) {
      setMsg(e.message || 'Erro ao anexar documento');
    } finally {
      setEnviandoAnexo(false);
    }
  };

  const resumoEvento = (et) => {
    const evs = Array.isArray(et?.eventos) ? et.eventos : [];
    const ultimo = evs[evs.length - 1];
    return {
      responsavel: ultimo?.usuario || et?.grupoResponsavel || '—',
      data: formatDt(et?.concluidoEm || et?.iniciadoEm || ultimo?.createdAt),
      obs: ultimo?.observacao || '',
    };
  };

  if (loadingBase || loading) {
    return (
      <div className="mf-page" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <CircularProgress sx={{ color: '#5ce1c5' }} />
      </div>
    );
  }

  if (errorBase || !fiscal || !processo) {
    return (
      <div className="mf-page">
        <p className="mf-page__meta">{msg || errorBase || 'Processo não encontrado.'}</p>
        <button type="button" className="mf-page__back" onClick={() => navigate(basePath)}>
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="mf-page">
      <div className="mf-page__header">
        <div>
          <h1 className="mf-page__title">{processo.codigo}</h1>
          <p className="mf-page__meta">
            {processo.titulo}
            <br />
            Etapa atual:{' '}
            <strong style={{ color: '#5ce1c5' }}>{etapaAtual?.nome || '—'}</strong>
            {' · '}
            Status: {processo.status}
          </p>
        </div>
        <button type="button" className="mf-page__back" onClick={() => navigate(basePath)}>
          Voltar
        </button>
      </div>

      <TrilhaStepperHeader
        etapas={etapasTrilha}
        etapaAtualId={etapaAtual?.id}
        etapaSelecionadaId={etapaSelecionadaId ?? etapaAtual?.id}
        onSelectEtapa={(et) => {
          setAba('etapa');
          if (et.ehAtual || Number(et.id) === Number(etapaAtual?.id)) {
            setEtapaSelecionadaId(null);
          } else if (String(et.status).toUpperCase() === 'CONCLUIDO') {
            setEtapaSelecionadaId(et.id);
          }
        }}
      />

      {msg ? (
        <p
          className={`mf-alert ${
            msg.includes('Erro') || msg.includes('Falha') ? 'mf-alert--error' : 'mf-alert--ok'
          }`}
        >
          {msg}
        </p>
      ) : null}

      {!perm.canAct ? (
        <p className="mf-alert mf-alert--warn">{perm.mensagem}</p>
      ) : (
        <p className="mf-alert mf-alert--ok">
          Você é responsável por esta etapa (conforme Configuração de Fluxo).
          {movendo ? ' Processando…' : ''}
        </p>
      )}

      <div className="mf-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`mf-tabs__btn${aba === 'etapa' ? ' mf-tabs__btn--active' : ''}`}
          onClick={() => setAba('etapa')}
        >
          Etapa
        </button>
        <button
          type="button"
          role="tab"
          className={`mf-tabs__btn${aba === 'historico' ? ' mf-tabs__btn--active' : ''}`}
          onClick={() => setAba('historico')}
        >
          Histórico completo
        </button>
      </div>

      {aba === 'historico' ? (
        <div className="mf-card mf-card--light">
          <ProcessoHistoricoTimeline
            timeline={timeline}
            tempoTotalDias={timelineMeta.tempoTotalDias}
            tempoTotalHoras={timelineMeta.tempoTotalHoras}
            showRadar={false}
          />
        </div>
      ) : (
        <>
          {etapasConcluidas.length > 0 && (
            <div className="mf-card" style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                className="mf-collapse-toggle"
                onClick={() => setConcluidasOpen((v) => !v)}
              >
                {concluidasOpen ? '▾' : '▸'} Ver {etapasConcluidas.length} etapa
                {etapasConcluidas.length > 1 ? 's' : ''} já concluída
                {etapasConcluidas.length > 1 ? 's' : ''}
              </button>
              {concluidasOpen && (
                <ul className="mf-concluidas-list">
                  {etapasConcluidas.map((et) => {
                    const r = resumoEvento(et);
                    return (
                      <li key={et.id}>
                        <button
                          type="button"
                          className="mf-concluidas-item"
                          onClick={() => setEtapaSelecionadaId(et.id)}
                        >
                          <strong>{et.nome}</strong>
                          <span>
                            {r.responsavel} · {r.data}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {!vendoEtapaAtual && etapaSelecionada ? (
            <div className="mf-card">
              <p className="mf-page__meta" style={{ marginBottom: '0.75rem' }}>
                Visualizando etapa concluída (somente leitura).
              </p>
              <h2 className="mf-section-title" style={{ marginTop: 0 }}>
                {etapaSelecionada.nome}
              </h2>
              {(() => {
                const r = resumoEvento(etapaSelecionada);
                return (
                  <div className="mf-resumo-etapa">
                    <p>
                      <strong>Responsável:</strong> {r.responsavel}
                    </p>
                    <p>
                      <strong>Data:</strong> {r.data}
                    </p>
                    {r.obs ? (
                      <p>
                        <strong>Observação:</strong> {r.obs}
                      </p>
                    ) : null}
                  </div>
                );
              })()}
              <button
                type="button"
                className="mf-btn mf-btn--primary"
                style={{ marginTop: '1rem' }}
                onClick={() => setEtapaSelecionadaId(null)}
              >
                Voltar à etapa atual
              </button>
            </div>
          ) : (
            <>
              <p className="mf-objetivo">{fraseObjetivoEtapa(tipoAcao, etapaAtual?.nome)}</p>

              <div className="mf-card" style={{ marginBottom: '1rem' }}>
                <h3 className="mf-section-title" style={{ marginTop: 0 }}>
                  Anexar documento (opcional)
                </h3>
                <p className="mf-page__meta" style={{ marginBottom: '0.75rem' }}>
                  PDF desta etapa — não é obrigatório. Pode enviar agora ou junto com aprovar/avançar.
                </p>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  disabled={!perm.canAct || enviandoAnexo || movendo}
                  onChange={(e) => {
                    const list = Array.from(e.target.files || []);
                    setAnexosEtapa(list);
                    e.target.value = '';
                  }}
                />
                {anexosEtapa.length > 0 && (
                  <ul className="mf-page__meta" style={{ marginTop: '0.5rem' }}>
                    {anexosEtapa.map((f) => (
                      <li key={`${f.name}-${f.size}`}>{f.name}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="mf-btn"
                    disabled={!perm.canAct || !anexosEtapa.length || enviandoAnexo}
                    onClick={enviarAnexosAgora}
                  >
                    {enviandoAnexo ? 'Enviando…' : 'Enviar anexos agora'}
                  </button>
                  {anexosEtapa.length > 0 && (
                    <button
                      type="button"
                      className="mf-btn"
                      onClick={() => setAnexosEtapa([])}
                      disabled={enviandoAnexo}
                    >
                      Limpar seleção
                    </button>
                  )}
                </div>
              </div>

              {painel === 'EDITAL' && (
                <div className="mf-card mf-card--light">
                  <h3 className="mf-section-title" style={{ marginTop: 0 }}>
                    {etapaAtual?.nome || 'Edital'}
                  </h3>
                  {perm.canAct ? (
                    <PainelEditalContratacao
                      processoId={processo.id}
                      limiteReaberturas={etapaAtual?.limiteReaberturas || 3}
                      prazoAlertaDias={etapaAtual?.prazoAlertaDias || 3}
                      confirmLabel="Salvar rascunho"
                      onSaved={() => load()}
                    />
                  ) : (
                    <p className="mf-page__meta">Sem permissão de ação nesta etapa.</p>
                  )}
                </div>
              )}

              {painel === 'MAPA' && (
                <div className="mf-card mf-card--light">
                  <h3 className="mf-section-title" style={{ marginTop: 0 }}>
                    {etapaAtual?.nome || 'Propostas / Mapa'}
                  </h3>
                  <MapaComparativoPropostasPanel
                    processoId={processo.id}
                    onConcluido={() => load()}
                  />
                </div>
              )}

              {painel === 'MINUTA' && (
                <div className="mf-card">
                  <h3 className="mf-section-title" style={{ marginTop: 0 }}>
                    {etapaAtual?.nome || 'Minuta'}
                  </h3>
                  <p className="mf-page__meta">
                    Minuta com cláusulas travadas (Contratos) + IA. Ao concluir, o contrato alimenta{' '}
                    <strong>Prestadores</strong>.
                  </p>
                  {perm.canAct ? (
                    <button
                      type="button"
                      className="mf-btn mf-btn--primary"
                      onClick={() => setMinutaOpen(true)}
                      style={{ marginTop: '0.75rem' }}
                    >
                      Abrir editor de minuta
                    </button>
                  ) : (
                    <p className="mf-page__meta">Sem permissão de ação nesta etapa.</p>
                  )}
                </div>
              )}

              {painel === 'FORMULARIO' && (
                <div className="mf-card">
                  <h3 className="mf-section-title" style={{ marginTop: 0 }}>
                    {etapaAtual?.nome || 'Formulário'}
                  </h3>
                  <p className="mf-page__meta">
                    Preenchimento de formulário desta etapa. Use aprovar/avançar quando concluir.
                  </p>
                  {perm.canAct && (
                    <div className="mf-card mf-card--light" style={{ marginTop: '1rem' }}>
                      <PainelEtapaAprovacaoFluxo
                        processoId={processo.id}
                        tipoAcao="APROVACAO_SIMPLES"
                        tituloEtapa={etapaAtual?.nome || ''}
                        dadosSolicitante={{
                          codigo: processo.codigo,
                          titulo: processo.titulo,
                          unidade: processo.unidade,
                        }}
                        onSaved={() => load()}
                        onDecisao={executarMover}
                      />
                    </div>
                  )}
                </div>
              )}

              {painel === 'APROVACAO' && (
                <div className="mf-card mf-card--light">
                  {perm.canAct ? (
                    <PainelEtapaAprovacaoFluxo
                      processoId={processo.id}
                      tipoAcao={tipoAcao || 'APROVACAO_COM_ASSINATURA'}
                      tituloEtapa={etapaAtual?.nome || ''}
                      dadosSolicitante={{
                        codigo: processo.codigo,
                        titulo: processo.titulo,
                        unidade: processo.unidade,
                      }}
                      onSaved={() => load()}
                      onDecisao={executarMover}
                    />
                  ) : (
                    <p className="mf-page__meta">Aguardando responsável desta etapa.</p>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      <EditorTRModal
        open={minutaOpen}
        onClose={() => {
          setMinutaOpen(false);
          load();
        }}
        processoId={processo.id}
        fluxoId={processo.fluxoId || processo.fluxo_id}
        instrumentoTipo="minuta"
        assuntoCI={processo.titulo}
      />
    </div>
  );
}
